import type { QueryClient } from "@tanstack/react-query";

const isNumericIdentifier = (value: unknown): boolean => {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      return false;
    }
    return Number.isFinite(Number(trimmed));
  }

  return false;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const isPostsQueryKey = (queryKey: readonly unknown[]): boolean => {
  if (queryKey.length === 1) {
    return true;
  }

  if (queryKey.length === 2) {
    const second = queryKey[1];
    if (second === "list") {
      return true;
    }
    return isNumericIdentifier(second);
  }

  if (queryKey.length === 3 && typeof queryKey[1] === "string") {
    const segment = queryKey[1];
    if (["list", "detail", "comments", "comment"].includes(segment)) {
      return isNumericIdentifier(queryKey[2]);
    }
  }

  return false;
};

const isPinnedPostsQueryKey = (queryKey: readonly unknown[]): boolean => {
  if (queryKey.length === 1) {
    return true;
  }

  if (queryKey.length === 2) {
    return queryKey[1] === "list";
  }

  if (queryKey.length === 3 && queryKey[1] === "list") {
    return isNumericIdentifier(queryKey[2]);
  }

  return false;
};

const isActivitiesQueryKey = (queryKey: readonly unknown[]): boolean => {
  if (queryKey.length === 1) {
    return true;
  }

  if (queryKey.length === 2) {
    return isPlainObject(queryKey[1]);
  }

  return false;
};

const isPostDetailQueryKey = (queryKey: readonly unknown[]): boolean => {
  if (queryKey.length === 1) {
    return true;
  }

  if (queryKey.length === 2) {
    return isNumericIdentifier(queryKey[1]);
  }

  return false;
};

const isFavoritePostsQueryKey = (queryKey: readonly unknown[]): boolean => {
  if (queryKey.length === 1) {
    return true;
  }

  if (queryKey.length === 2) {
    return queryKey[1] === "list";
  }

  return false;
};

const isStudyGroupMembershipsQueryKey = (
  queryKey: readonly unknown[],
): boolean => {
  if (queryKey.length === 0) return false;
  return queryKey[0] === "studyGroupMemberships";
};

const isStudyGroupAdminsQueryKey = (queryKey: readonly unknown[]): boolean => {
  if (queryKey.length === 0) return false;
  return queryKey[0] === "studyGroupAdmins";
};

const chatQueryKeys = new Set([
  "chatRooms",
  "chatRoomsInfinite",
  "chatRoomsUnreadTotals",
  "chatRoom",
  "chatRoomMembers",
  "chatRoomEntries",
  "chatMentionUsers",
]);

const isExistingChatQueryKey = (queryKey: readonly unknown[]): boolean => {
  return queryKey[0] === "chatExistingByParticipants";
};

const shouldInvalidateChatQuery = (
  queryKey: readonly unknown[],
  chatRoomId?: string,
) => {
  const key = queryKey[0] as string;

  if (!chatQueryKeys.has(key)) {
    return false;
  }

  if (
    key === "chatRooms" ||
    key === "chatRoomsUnreadTotals" ||
    key === "chatRoomsInfinite"
  ) {
    return true;
  }

  if (!chatRoomId) {
    return true;
  }

  if (queryKey[1] && queryKey[1] !== chatRoomId) {
    return false;
  }

  return true;
};

/**
 * クエリ無効化のヘルパー関数
 * メンテナンス性を向上させるため、無効化ロジックを集約
 */
export const queryInvalidationHelpers = {
  // スタディグループ関連の全クエリを無効化
  invalidateStudyGroupQueries: (
    queryClient: QueryClient,
    studyGroupId?: string | number,
  ) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0] as string;
        const studyGroupKeys = [
          "studyGroups",
          "myStudyGroups",
          "studyGroup",
          "studyGroupMemberships",
          "studyGroupYourMembership",
          "studyGroupTags",
        ];

        // 基本的なスタディグループ関連クエリ
        if (studyGroupKeys.includes(key)) {
          // 特定のIDが指定されている場合は、そのIDに関連するもののみ
          if (
            studyGroupId &&
            query.queryKey[1] &&
            query.queryKey[1] !== studyGroupId
          ) {
            return false;
          }
          return true;
        }

        return false;
      },
    });
  },

  // myStudyGroupsクエリ（全ステータス）を無効化
  invalidateMyStudyGroupsQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0] as string;
        return key === "myStudyGroups";
      },
    });
  },

  // 招待関連の全クエリを無効化
  invalidateInvitationQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0] as string;
        return [
          "invitations",
          "pendingInvitations",
          "sentInvitations",
        ].includes(key);
      },
    });
  },

  // チャット関連の全クエリを無効化
  invalidateChatQueries: (queryClient: QueryClient, chatRoomId?: string) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        return shouldInvalidateChatQuery(query.queryKey, chatRoomId);
      },
    });
  },

  // 既存チャット検索（参加者ベース）を無効化
  invalidateExistingChatQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => isExistingChatQueryKey(query.queryKey),
    });
  },

  // チャットメンバーシップ変更時の無効化（メンバー追加、削除など）
  invalidateOnChatMembershipChange: (
    queryClient: QueryClient,
    chatRoomId?: string,
  ) => {
    queryInvalidationHelpers.invalidateChatQueries(queryClient, chatRoomId);
    queryInvalidationHelpers.invalidateExistingChatQueries(queryClient);
  },

  // メンバーシップ変更時の無効化（参加、脱退、招待受諾など）
  invalidateOnMembershipChange: (
    queryClient: QueryClient,
    studyGroupId?: string | number,
  ) => {
    // 従来の無効化処理
    queryInvalidationHelpers.invalidateStudyGroupQueries(
      queryClient,
      studyGroupId,
    );
    queryInvalidationHelpers.invalidateInvitationQueries(queryClient);

    // ★ 追加: myStudyGroups（全ステータス）の無効化
    queryInvalidationHelpers.invalidateMyStudyGroupsQueries(queryClient);

    // チャットクエリの無効化（招待メッセージが即座に表示されるように）
    queryInvalidationHelpers.invalidateChatQueries(queryClient);
  },

  // イベント関連の無効化
  invalidateEventQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0] as string;
        return [
          "events",
          "groupEvents",
          "myEvents",
          "registeredEvents",
        ].includes(key);
      },
    });
  },

  // ファイル関連の無効化
  invalidateFileQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0] as string;
        return ["files", "folders", "fileBlob"].includes(key);
      },
    });
  },

  // ブロック／アンブロック時に表示から除外すべきデータ群の無効化
  invalidateOnUserBlockChange: (
    queryClient: QueryClient,
    options?: {
      userId?: number | string;
      invalidateUserInfo?: boolean;
    },
  ) => {
    const predicate = (query: { queryKey: readonly unknown[] }) => {
      if (!Array.isArray(query.queryKey) || query.queryKey.length === 0)
        return false;
      const key = query.queryKey[0];
      if (typeof key !== "string") return false;

      switch (key) {
        case "posts":
          return isPostsQueryKey(query.queryKey);
        case "pinnedPosts":
          return isPinnedPostsQueryKey(query.queryKey);
        case "activities":
          return isActivitiesQueryKey(query.queryKey);
        case "postDetail":
          return isPostDetailQueryKey(query.queryKey);
        case "favoritePosts":
          return isFavoritePostsQueryKey(query.queryKey);
        case "studyGroupMemberships":
          return isStudyGroupMembershipsQueryKey(query.queryKey);
        case "studyGroupAdmins":
          return isStudyGroupAdminsQueryKey(query.queryKey);
        default:
          return false;
      }
    };

    queryClient.invalidateQueries({ predicate });
    queryClient.refetchQueries({ predicate, type: "active" });

    if (options?.invalidateUserInfo) {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["users", "current"] });
      queryClient.invalidateQueries({ queryKey: ["users", "profile"] });
      if (options.userId != null) {
        queryClient.invalidateQueries({
          queryKey: ["users", "detail", options.userId],
        });
      }
    }
  },
};
